import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import {
  getOrganizationTypes,
  createOrganization,
  getOrganizations,
  linkOrganizationToLocation,
  getCanadaProvinces,
  getCanadaMunicipalities,
  getCanadaNeighbourhoods
} from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  Building2,
  Save,
  AlertCircle,
  Globe,
  Share2
} from 'lucide-react';
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary
} from '../../components/layout';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { toast } from '../../hooks/use-toast';
import { SocialLinksEditor } from './SocialLinksEditor';
import {
  OrganizationFormData,
  SocialLink,
  SchoolHours,
  DEFAULT_SCHOOL_HOURS,
  DAYS_OF_WEEK,
  FORMAT_OPTIONS,
  PROGRAMS_OFFERED_OPTIONS,
  AGE_RANGE_OPTIONS,
} from './organizationTypes';

interface FormErrors {
  name?: string;
  slug?: string;
  organizationTypeId?: string;
  submit?: string;
}

const FormSection = ({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
    <div className="pb-1 border-b border-gray-100">
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
    </div>
    {children}
  </div>
);

export default function AddOrganizationPage() {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const { locations } = useLocationContext();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [activeTab, setActiveTab] = useState<'details' | 'location' | 'services' | 'social'>('details');

  // Location selection state
  const [selectedProvinceId, setSelectedProvinceId] = useState('');
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState('');
  const [selectedNeighbourhoodId, setSelectedNeighbourhoodId] = useState('');
  const [isNational, setIsNational] = useState(false);

  const { data: organizationTypes = [] } = useQuery(getOrganizationTypes);
  const { data: organizations = [] } = useQuery(getOrganizations);
  const { data: provinces = [] } = useQuery(getCanadaProvinces);
  const { data: municipalities = [] } = useQuery(getCanadaMunicipalities, {
    provinceId: selectedProvinceId || undefined
  });
  const { data: neighbourhoods = [] } = useQuery(getCanadaNeighbourhoods, {
    municipalityId: selectedMunicipalityId || undefined
  });

  const createOrganizationFn = useAction(createOrganization);
  const linkOrganizationToLocationFn = useAction(linkOrganizationToLocation);

  const [formData, setFormData] = useState<OrganizationFormData & { locationIds: string[] }>({
    oid: '',
    name: '',
    slug: '',
    organizationTypeId: '',
    parentOrganizationId: '',
    provinceId: '',
    cityId: '',
    address: '',
    postalCode: '',
    contact: '',
    description: '',
    website: '',
    email: '',
    phone: '',
    mediaScope: '',
    subType: '',
    format: '',
    language: '',
    languages: [],
    notes: '',
    isActive: true,
    socialLinks: [],
    serviceDefinitionIds: [],
    hiddenSections: [],
    sidebarOverrideEnabled: false,
    locationIds: [],
    // Type-specific extra fields
    craRegistration: '',
    emergencyPhone: '',
    typeFormat: [],
    licensesAccreditation: '',
    packageTypes: '',
    programsOffered: [],
    ageRange: [],
    durationSchedule: '',
    programType: '',
    schoolHours: { ...DEFAULT_SCHOOL_HOURS },
    CommunityFinanceModel: '',
    shariahCertification: ''
  });

  // Resolve a Canada reference name to a real Location.id by name + hierarchyLevel
  const resolveLocationId = (name: string, hierarchyLevel: number): string | null => {
    const loc = (locations as any[]).find(
      (l) => l.hierarchyLevel === hierarchyLevel && l.name.toLowerCase() === name.toLowerCase()
    );
    return loc?.id ?? null;
  };

  // When province changes, reset downstream selections
  const handleProvinceChange = (provinceId: string) => {
    setSelectedProvinceId(provinceId);
    setSelectedMunicipalityId('');
    setSelectedNeighbourhoodId('');
    const province = (provinces as any[]).find((p: any) => p.id === provinceId);
    const locationId = province ? resolveLocationId(province.name, 1) : null;
    setFormData(f => ({ ...f, provinceId: provinceId || '', cityId: '', locationIds: locationId ? [locationId] : [] }));
  };

  // When municipality changes, reset neighbourhood
  const handleMunicipalityChange = (municipalityId: string) => {
    setSelectedMunicipalityId(municipalityId);
    setSelectedNeighbourhoodId('');
    const municipality = (municipalities as any[]).find((m: any) => m.id === municipalityId);
    const locationId = municipality ? resolveLocationId(municipality.name, 2) : null;
    setFormData(f => ({ ...f, cityId: municipalityId || '', locationIds: locationId ? [locationId] : [] }));
  };

  const handleNeighbourhoodChange = (neighbourhoodId: string) => {
    setSelectedNeighbourhoodId(neighbourhoodId);
    const neighbourhood = (neighbourhoods as any[]).find((n: any) => n.id === neighbourhoodId);
    if (neighbourhood) {
      const locationId = resolveLocationId(neighbourhood.name, 3);
      if (locationId) {
        setFormData(f => ({ ...f, locationIds: [locationId] }));
        return;
      }
    }
    // Cleared or not found — fall back to municipality
    const municipality = (municipalities as any[]).find((m: any) => m.id === selectedMunicipalityId);
    const fallbackId = municipality ? resolveLocationId(municipality.name, 2) : null;
    setFormData(f => ({ ...f, locationIds: fallbackId ? [fallbackId] : [] }));
  };

  const handleNationalToggle = (checked: boolean) => {
    setIsNational(checked);
  };

  const handleNameChange = (name: string) => {
    setFormData(f => ({
      ...f,
      name,
      slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    }));
    if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
  };

  const handleChange = (
    field: keyof typeof formData,
    value: string | boolean | string[] | SocialLink[] | SchoolHours
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleMultiToggle = (field: 'typeFormat' | 'programsOffered' | 'ageRange', option: string) => {
    setFormData(prev => {
      const current = prev[field] as string[];
      const updated = current.includes(option)
        ? current.filter(v => v !== option)
        : [...current, option];
      return { ...prev, [field]: updated };
    });
  };

  const handleSchoolHoursChange = (day: string, key: 'enabled' | 'start' | 'end', value: boolean | string) => {
    setFormData(prev => ({
      ...prev,
      schoolHours: {
        ...prev.schoolHours,
        [day]: { ...prev.schoolHours[day], [key]: value }
      }
    }));
  };

  const parentOrganizations = (organizations as any[]).filter((org: any) => !org.parentOrganizationId);

  const selectedType = (organizationTypes as any[]).find((t: any) => t.id === formData.organizationTypeId);
  const isMediaType = selectedType?.key === 'media';
  const isCharityOrSocial = ['charities', 'social-services'].includes(selectedType?.key);
  const isFuneralType = selectedType?.key === 'funeral-services';
  const isMediaPublicationsType = selectedType?.key === 'media-publications';
  const isPilgrimageType = selectedType?.key === 'Pilgrimage-umrah';
  const isWeekendSchoolType = selectedType?.key === 'weekend-schools';
  const isYouthProgramType = selectedType?.key === 'youth-programs';
  const isCommunitySchoolType = selectedType?.key === 'Community-schools';
  const isFinancialType = selectedType?.key === 'financial-services';

  const buildTypeFieldsSettings = () => {
    const tf: any = {};
    if (isCharityOrSocial && formData.craRegistration.trim()) tf.craRegistration = formData.craRegistration.trim();
    if (isFuneralType && formData.emergencyPhone.trim()) tf.emergencyPhone = formData.emergencyPhone.trim();
    if (isMediaPublicationsType && formData.typeFormat.length) tf.format = formData.typeFormat;
    if (isPilgrimageType && formData.licensesAccreditation.trim()) tf.licensesAccreditation = formData.licensesAccreditation.trim();
    if (isPilgrimageType && formData.packageTypes.trim()) tf.packageTypes = formData.packageTypes.trim();
    if (isWeekendSchoolType && formData.programsOffered.length) tf.programsOffered = formData.programsOffered;
    if ((isWeekendSchoolType || isYouthProgramType) && formData.ageRange.length) tf.ageRange = formData.ageRange;
    if (isWeekendSchoolType && formData.durationSchedule.trim()) tf.durationSchedule = formData.durationSchedule.trim();
    if (isYouthProgramType && formData.programType.trim()) tf.programType = formData.programType.trim();
    if (isCommunitySchoolType) tf.schoolHours = formData.schoolHours;
    if (isFinancialType && formData.CommunityFinanceModel.trim()) tf.CommunityFinanceModel = formData.CommunityFinanceModel.trim();
    if (isFinancialType && formData.shariahCertification.trim()) tf.shariahCertification = formData.shariahCertification.trim();
    return Object.keys(tf).length > 0 ? tf : undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Organization name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.organizationTypeId) newErrors.organizationTypeId = 'Organization type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildMediaSettings = () => {
    const media = {
      scope: formData.mediaScope.trim() || undefined,
      subType: formData.subType.trim() || undefined,
      format: formData.format.trim() || undefined,
      language: formData.language.trim() || undefined,
      notes: formData.notes.trim() || undefined
    };
    const hasMedia = Object.values(media).some(v => v !== undefined);
    return hasMedia ? media : undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const org = await createOrganizationFn({
        oid: formData.oid.trim() || undefined,
        name: formData.name,
        slug: formData.slug,
        organizationTypeId: formData.organizationTypeId,
        parentOrganizationId: formData.parentOrganizationId || undefined,
        description: formData.description || undefined,
        address: formData.address.trim() || undefined,
        postalCode: formData.postalCode.trim() || undefined,
        website: formData.website.trim() || undefined,
        contact: formData.contact.trim() || undefined,
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        languages: formData.languages,
        provinceId: formData.provinceId || undefined,
        cityId: formData.cityId || undefined,
        isNational,
        media: buildMediaSettings(),
        typeFields: buildTypeFieldsSettings(),
        social: formData.socialLinks.length > 0 ? formData.socialLinks : undefined,
        serviceDefinitionIds: formData.serviceDefinitionIds.length > 0 ? formData.serviceDefinitionIds : undefined,
        isActive: formData.isActive
      });

      if (formData.locationIds.length > 0) {
        await Promise.all(
          formData.locationIds.map((locationId) =>
            linkOrganizationToLocationFn({ locationId, organizationId: org.id })
          )
        );
      }

      toast({ title: 'Success', description: 'Organization created successfully' });
      navigate(`/admin/organizations/${org.id}`);
    } catch (error: any) {
      console.error('Error creating organization:', error);
      setErrors({ submit: error.message || 'Failed to create organization. Please try again.' });
      toast({
        title: 'Error',
        description: error.message || 'Failed to create organization',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to add organizations.</p>
        </div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <ActionButtons>
          <ActionButton
            type="submit"
            form="organization-form"
            disabled={isLoading}
            icon={Save}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Creating...
              </>
            ) : (
              'Create'
            )}
          </ActionButton>
          <ActionButtonSecondary
            onClick={() => navigate('/admin/organizations')}
            disabled={isLoading}
          >
            Cancel
          </ActionButtonSecondary>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Settings">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
          />
          <span className="text-sm text-gray-700">Active Organization</span>
        </label>
      </SidebarSection>

      <SidebarSection title="Help">
        <div className="space-y-1 text-sm text-gray-600">
          <p>• Name, slug, and type are required</p>
          <p>• Slug is auto-generated from name</p>
          <p>• Mark as National to show under Canada at the top level</p>
          <p>• Select province/city to link to a specific location</p>
          <p>• Click platform chips to add social links</p>
          <p>• Select services this organization offers</p>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout sidebarContent={sidebarContent} layoutVariant="taxonomy" authUser={user}>
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <Building2 className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add Organization</h1>
            <p className="text-gray-600">Create a new organization in the system</p>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab('details')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'details'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Building2 className="w-4 h-4" />
          Details
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('location')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'location'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Globe className="w-4 h-4" />
          Location
          {(isNational || selectedProvinceId) && (
            <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
              {isNational ? 'National' : (provinces as any[]).find((p: any) => p.id === selectedProvinceId)?.code ?? ''}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('services')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'services'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Languages
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('social')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'social'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Share2 className="w-4 h-4" />
          Social
        </button>
      </div>

      <form id="organization-form" onSubmit={handleSubmit} className="space-y-6">

        {/* Details Tab */}
        {activeTab === 'details' && <>

        {/* Basic Info */}
        <FormSection title="Basic Info">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">OID</label>
              <input
                type="text"
                value={formData.oid}
                onChange={(e) => handleChange('oid', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="ORG-001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.name ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                placeholder="e.g., Community Center of Toronto"
                required
              />
              {errors.name && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />{errors.name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.slug ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                placeholder="Community-center-toronto"
                required
              />
              {errors.slug && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />{errors.slug}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">Auto-generated from name, or customize it</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.organizationTypeId}
                onChange={(e) => handleChange('organizationTypeId', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.organizationTypeId ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                required
              >
                <option value="">Select Type</option>
                {(organizationTypes as any[]).map((type: any) => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
              {errors.organizationTypeId && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />{errors.organizationTypeId}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Parent Organization</label>
              <select
                value={formData.parentOrganizationId}
                onChange={(e) => handleChange('parentOrganizationId', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              >
                <option value="">None</option>
                {parentOrganizations.map((org: any) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Optional: select if this is a sub-organization</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              placeholder="Provide a description of the organization..."
            />
            <p className="text-xs text-gray-500 mt-1">{formData.description.length} characters</p>
          </div>
        </FormSection>

        {/* Media Details — only for media organization type */}
        {isMediaType && <FormSection title="Media Details" description="Additional details for media organizations.">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Scope</label>
              <input
                type="text"
                value={formData.mediaScope}
                onChange={(e) => handleChange('mediaScope', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="National"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sub-Type</label>
              <input
                type="text"
                value={formData.subType}
                onChange={(e) => handleChange('subType', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Digital Magazine"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
              <input
                type="text"
                value={formData.format}
                onChange={(e) => handleChange('format', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Website + Newsletter"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
              <input
                type="text"
                value={formData.language}
                onChange={(e) => handleChange('language', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="English"
              />
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Additional notes or context..."
              />
            </div>
          </div>
        </FormSection>}

        {/* Charity / Social Services */}
        {isCharityOrSocial && <FormSection title="Charity Details" description="Additional details for charitable and social service organizations.">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">CRA Registration Number</label>
            <input
              type="text"
              value={formData.craRegistration}
              onChange={(e) => handleChange('craRegistration', e.target.value)}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              placeholder="123456789 RR 0001"
            />
          </div>
        </FormSection>}

        {/* Funeral & Burial Services */}
        {isFuneralType && <FormSection title="Funeral Service Details" description="Additional details for funeral and burial service organizations.">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Phone</label>
            <input
              type="tel"
              value={formData.emergencyPhone}
              onChange={(e) => handleChange('emergencyPhone', e.target.value)}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              placeholder="+1 (416) 555-0100"
            />
          </div>
        </FormSection>}

        {/* Media & Publications */}
        {isMediaPublicationsType && <FormSection title="Media & Publications Details" description="Additional details for media and publication organizations.">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {FORMAT_OPTIONS.map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleMultiToggle('typeFormat', opt)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    formData.typeFormat.includes(opt)
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
            {formData.typeFormat.length > 0 && (
              <p className="text-xs text-gray-500 mt-2">Selected: {formData.typeFormat.join(', ')}</p>
            )}
          </div>
        </FormSection>}

        {/* Pilgrimage & Umrah Agencies */}
        {isPilgrimageType && <FormSection title="Pilgrimage & Umrah Details" description="Additional details for Pilgrimage and Umrah agencies.">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Licenses & Accreditation</label>
              <input
                type="text"
                value={formData.licensesAccreditation}
                onChange={(e) => handleChange('licensesAccreditation', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="IATA, TICO, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Package Types</label>
              <textarea
                value={formData.packageTypes}
                onChange={(e) => handleChange('packageTypes', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Economy Package, Premium Package, Group Package..."
              />
              <p className="text-xs text-gray-500 mt-1">List each package type on a separate line or separated by commas</p>
            </div>
          </div>
        </FormSection>}

        {/* Weekend Community Schools */}
        {isWeekendSchoolType && <FormSection title="Weekend School Details" description="Additional details for weekend Community schools and education programs.">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Programs Offered</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PROGRAMS_OFFERED_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => handleMultiToggle('programsOffered', opt)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      formData.programsOffered.includes(opt)
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Age Range</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {AGE_RANGE_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => handleMultiToggle('ageRange', opt)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      formData.ageRange.includes(opt)
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration / Schedule</label>
              <input
                type="text"
                value={formData.durationSchedule}
                onChange={(e) => handleChange('durationSchedule', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Saturdays 9am–1pm"
              />
            </div>
          </div>
        </FormSection>}

        {/* Youth Programs & Camps */}
        {isYouthProgramType && <FormSection title="Youth Program Details" description="Additional details for youth programs and camps.">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Program Type</label>
              <input
                type="text"
                value={formData.programType}
                onChange={(e) => handleChange('programType', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Summer Camp, After-School, Weekend Program"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Age Range</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {AGE_RANGE_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => handleMultiToggle('ageRange', opt)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      formData.ageRange.includes(opt)
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </FormSection>}

        {/* Community Schools (Full-Time) */}
        {isCommunitySchoolType && <FormSection title="School Hours" description="Set operating hours for each day of the week.">
          <div className="space-y-2">
            {DAYS_OF_WEEK.map(day => (
              <div key={day} className="flex items-center gap-4">
                <label className="flex items-center gap-2 w-32 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.schoolHours[day]?.enabled ?? false}
                    onChange={(e) => handleSchoolHoursChange(day, 'enabled', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">{day.slice(0, 3)}</span>
                </label>
                {formData.schoolHours[day]?.enabled ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={formData.schoolHours[day]?.start ?? '08:00'}
                      onChange={(e) => handleSchoolHoursChange(day, 'start', e.target.value)}
                      className="px-2 py-1.5 border border-input rounded-lg text-sm focus:ring-2 focus:ring-ring"
                    />
                    <span className="text-gray-400 text-sm">to</span>
                    <input
                      type="time"
                      value={formData.schoolHours[day]?.end ?? '16:00'}
                      onChange={(e) => handleSchoolHoursChange(day, 'end', e.target.value)}
                      className="px-2 py-1.5 border border-input rounded-lg text-sm focus:ring-2 focus:ring-ring"
                    />
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">Closed</span>
                )}
              </div>
            ))}
          </div>
        </FormSection>}

        {/* Community Financial Services */}
        {isFinancialType && <FormSection title="Community Finance Details" description="Additional details for Community financial service organizations.">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Community Finance Model</label>
              <input
                type="text"
                value={formData.CommunityFinanceModel}
                onChange={(e) => handleChange('CommunityFinanceModel', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Murabaha, Ijara, Musharaka, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Shariah Certification</label>
              <input
                type="text"
                value={formData.shariahCertification}
                onChange={(e) => handleChange('shariahCertification', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Certified by AAOIFI, ISNA, etc."
              />
            </div>
          </div>
        </FormSection>}

        </>}

        {/* Location Tab */}
        {activeTab === 'location' && (
          <>
          <FormSection title="Location" description="Select the geographic scope for this organization.">
            {/* National toggle */}
            <label className="flex items-center gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50 cursor-pointer">
              <input
                type="checkbox"
                checked={isNational}
                onChange={(e) => handleNationalToggle(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
              />
              <Globe className="w-4 h-4 text-blue-600" />
              <div>
                <span className="text-sm font-medium text-blue-900">National Organization</span>
                <p className="text-xs text-blue-700">This organization operates across Canada and will appear at the country level</p>
              </div>
            </label>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="123 Main St, Toronto, ON"
                />
              </div>

              {/* Province */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Province / Territory</label>
                <select
                  value={selectedProvinceId}
                  onChange={(e) => handleProvinceChange(e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                >
                  <option value="">Select Province</option>
                  {(provinces as any[]).map((p: any) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* City / Municipality */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City / Town</label>
                <select
                  value={selectedMunicipalityId}
                  onChange={(e) => handleMunicipalityChange(e.target.value)}
                  disabled={!selectedProvinceId}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors disabled:opacity-50 disabled:bg-gray-50"
                >
                  <option value="">{selectedProvinceId ? 'Select City / Town' : 'Select a province first'}</option>
                  {(municipalities as any[]).map((m: any) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Neighbourhood */}
              {selectedMunicipalityId && (neighbourhoods as any[]).length > 0 && (
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Neighbourhood <span className="text-gray-400 font-normal">(optional)</span></label>
                  <select
                    value={selectedNeighbourhoodId}
                    onChange={(e) => handleNeighbourhoodChange(e.target.value)}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  >
                    <option value="">No specific neighbourhood</option>
                    {(neighbourhoods as any[]).map((n: any) => (
                      <option key={n.id} value={n.id}>{n.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {(selectedProvinceId || selectedMunicipalityId) && (
                <div className="lg:col-span-2">
                  <p className="text-xs text-gray-500">
                    Selected: {[
                      (provinces as any[]).find((p: any) => p.id === selectedProvinceId)?.name,
                      (municipalities as any[]).find((m: any) => m.id === selectedMunicipalityId)?.name,
                      (neighbourhoods as any[]).find((n: any) => n.id === selectedNeighbourhoodId)?.name
                    ].filter(Boolean).join(' › ')}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code</label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => handleChange('postalCode', e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="M5V 2T6"
                />
              </div>
            </div>

            {isNational && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <p className="text-sm text-blue-800">This organization will also appear under Canada at the national level.</p>
              </div>
            )}
          </FormSection>

          <FormSection title="Contact" description="Contact details for this organization.">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                <input
                  type="text"
                  value={formData.contact}
                  onChange={(e) => handleChange('contact', e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="Jane Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="info@organization.org"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="+1 (416) 555-0100"
                />
              </div>

              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="https://example.com"
                />
              </div>

            </div>
          </FormSection>
          </>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <>
          <FormSection title="Languages Offered" description="Languages this organization can serve in.">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Languages</label>
              <input
                type="text"
                value={formData.languages.join(', ')}
                onChange={(e) => handleChange('languages', e.target.value.split(',').map(v => v.trim()).filter(Boolean))}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="English, French, Native"
              />
              <p className="text-xs text-gray-500 mt-1">Comma-separated values</p>
            </div>
          </FormSection>

          </>
        )}

        {/* Social Tab */}
        {activeTab === 'social' && (
          <FormSection title="Social Media" description="Public social channels for this organization.">
            <SocialLinksEditor
              value={formData.socialLinks}
              onChange={(links) => handleChange('socialLinks', links)}
            />
          </FormSection>
        )}

        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}



